// The store's appointments for a day, shown in place of the style grid:
// who is coming in, when, and who with. Mark one done once the customer has
// been seen, or cancel it. Appointments are booked from the customer screen
// ("Book appointment"), because they belong to the customer's book.
// Online only: the customers' books are on the server.
import React, { useEffect, useRef, useState } from "react";
import "./FashionWorkPanels.css";
import "./FashionFollowUpsPanel.css";
import { fashionPosService } from "../../../services";
import { readMessage } from "../../../utils/fashion/holds";
import { buildCloseClientItemMutation, today } from "../../../utils/fashion/clientBook";
import { initials, telHref } from "../../../utils/fashion/followUps";
import { appointmentStats, appointmentStatus, appointmentTone, buildAppointmentsQuery, dayTitle, readAppointments, shiftDay, timeRange } from "../../../utils/fashion/appointments";
import useFashionToast, { Toast } from "./hooks/useFashionToast";
import EmptyState from "./EmptyState";

export default function FashionAppointmentsPanel({ tillData, isOnline, onClose, backLabel = "Back to styles" }) {
  const bunitId = tillData?.tillAccess?.csBunit?.csBunitId;
  const online = typeof isOnline === "boolean" ? isOnline : typeof navigator === "undefined" || navigator.onLine;
  const [day, setDay] = useState(() => today());
  const [list, setList] = useState({ status: "loading", rows: [] });
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [reload, setReload] = useState(0);
  const [toast, say] = useFashionToast();

  useEffect(() => {
    if (!online || !bunitId) return undefined;
    let live = true;
    setList((l) => ({ ...l, status: "loading" }));
    (async () => {
      let rows = null;
      try {
        rows = readAppointments(await fashionPosService(buildAppointmentsQuery(bunitId, day)));
      } catch {
        rows = null;
      }
      if (live) setList(rows === null ? { status: "error", rows: [] } : { status: "done", rows });
    })();
    return () => {
      live = false;
    };
  }, [online, bunitId, day, reload]);

  const close = async (item, status) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const res = readMessage(await fashionPosService(buildCloseClientItemMutation(item.id, status)), "closeFashionClientItem", "Couldn't update the appointment.");
      if (res.ok) {
        say(`${item.customerName || "Customer"}, ${timeRange(item)}: ${status === "DONE" ? "marked done" : "cancelled"}.`, "ok");
        setList((l) => ({ ...l, rows: l.rows.map((r) => (r.id === item.id ? { ...r, status } : r)) }));
        setReload((n) => n + 1);
      } else {
        say(res.message, "bad");
      }
    } catch {
      say("Couldn't update the appointment. Check the connection and try again.", "bad");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const stats = appointmentStats(list.rows);
  const title = dayTitle(day);

  const row = (item) => (
    <li key={item.id} className="fp-fu-row">
      <div className="fp-fu-top">
        <span className="fp-wp-av" aria-hidden="true">
          {initials(item.customerName)}
        </span>
        <span className="fp-fu-who">
          <b>{item.customerName || "Customer"}</b> {item.mobileNo && <span className="fp-wp-meta fp-num fp-fu-mob">{item.mobileNo}</span>}
          <span className="fp-wp-meta fp-num">
            {timeRange(item)}
            {item.staffName ? ` · with ${item.staffName}` : ""}
          </span>
        </span>
        <span className={`fp-wp-pill is-${appointmentTone(item)}`}>{appointmentStatus(item)}</span>
      </div>
      {item.text && <span className="fp-fu-text">{item.text}</span>}
      {item.status === "OPEN" && (
        <div className="fp-fu-acts">
          {telHref(item.mobileNo) && (
            <a className="fp-btn-ghost fp-btn-sm fp-fu-call" href={telHref(item.mobileNo)} aria-label={`Call ${item.customerName || "customer"}`}>
              Call
            </a>
          )}
          <button type="button" className="fp-btn-primary fp-btn-sm" onClick={() => close(item, "DONE")} disabled={busy} aria-label={`Mark done: ${item.customerName || "customer"} at ${timeRange(item)}`}>
            Mark done
          </button>
          <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => close(item, "CAN")} disabled={busy} aria-label={`Cancel: ${item.customerName || "customer"} at ${timeRange(item)}`}>
            Cancel
          </button>
        </div>
      )}
    </li>
  );

  return (
    <section className="fp-panel fp-fu" aria-label="Appointments">
      <div className="fp-panel-head">
        <h2 className="fp-panel-title">Appointments</h2>
        <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={onClose}>
          {backLabel}
        </button>
      </div>
      <Toast toast={toast} />
      {!online ? (
        <EmptyState icon="offline" title="You're offline" hint="Appointments need a connection, because customers' books are on the server. Reconnect the till and try again." />
      ) : (
        <div className="fp-fu-main">
          <div className="fp-fu-stats fp-num" aria-label="Summary">
            <div>
              <span>Booked</span>
              <b>{stats.booked}</b>
            </div>
            <div>
              <span>Done</span>
              <b>{stats.done}</b>
            </div>
            <div>
              <span>Cancelled</span>
              <b>{stats.cancelled}</b>
            </div>
            <div>
              <span>All</span>
              <b>{list.rows.length}</b>
            </div>
          </div>
          <div className="fp-wp-card fp-fu-card">
            <div className="fp-wp-head">
              <div className="fp-wp-seg" role="group" aria-label="Day">
                <button type="button" onClick={() => setDay((d) => shiftDay(d, -1))} aria-label="Previous day">
                  ‹
                </button>
                <button type="button" className="is-on" aria-live="polite" onClick={() => setDay(today())} title="Back to today">
                  {title === "Today" || title === "Tomorrow" || title === "Yesterday" ? `${title} · ${day.slice(8)}/${day.slice(5, 7)}` : title}
                </button>
                <button type="button" onClick={() => setDay((d) => shiftDay(d, 1))} aria-label="Next day">
                  ›
                </button>
              </div>
              <button type="button" className="fp-btn-ghost fp-btn-sm" onClick={() => setReload((n) => n + 1)} disabled={list.status === "loading"}>
                Refresh
              </button>
            </div>
            {list.status === "error" && (
              <p className="fp-wp-meta is-bad" role="alert">
                Couldn't load the appointments. Check the connection and tap Refresh.
              </p>
            )}
            <ul className="fp-fu-list" aria-label={`Appointments ${title}`} aria-busy={list.status === "loading" ? "true" : "false"}>
              {list.rows.length === 0 &&
                list.status !== "error" &&
                (list.status === "loading" ? (
                  <li className="fp-empty">Loading appointments…</li>
                ) : (
                  <EmptyState as="li" title={`No appointments ${title === "Today" || title === "Tomorrow" || title === "Yesterday" ? title.toLowerCase() : `on ${title}`}.`} hint="Book one from the customer's screen." />
                ))}
              {list.rows.map(row)}
            </ul>
          </div>
        </div>
      )}
    </section>
  );
}
